// Utility class for methods that can be used across the entire app

import {ICurrentUser} from "./auth/CurrentUserType";
import {EAIClient} from "Services/EAIClient";
import * as Sentry from "@sentry/react";
import {Student} from "Stores/commonStore";
import {AttachmentFile} from "Components/Common/AttachmentSelector";
import {reaction} from "mobx";
import {SnapshotStore} from "Stores/dashboardStore/snapshots";
import {FeatureGating} from "Stores/featureGating";
import {FeatureGating as FeatureGatingSWC} from "@/hooks/feature-gating";
import {LabelValueOption} from "Components/Common/SelectWrapper";
import {Buffer} from "buffer";


import CONFIG from "Config";

export class DeadlinePromise<T> extends Promise<T> {
    constructor(callback: (resolve: (value?: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void, timeoutMillis = 5000) {
        super((resolve, reject) => {
            setTimeout(() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return resolve();
            }, timeoutMillis);
            callback(resolve, reject);
        });
    }
}

export class Utils {
    static DEFAULT_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
    static DEFAULT_DATE_FORMAT = "YYYY-MM-DD";
    static RESET_SCHOOLS = [
        "demo2", "demo_es", "demo_med", "demo_pharm", "verify",
        "demo", "sandbox"];

    static urlPromise?: Promise<string>;

    static clearLocalstorage = () => {
        const keysToClear = [
            "isMasquerade",
            "current_user",
            "expires_at",
            "is_superadmin",
            "school_list",
            "chosen_school_info",
            // These keys are from the old auth flow. At some point we can remove these from this list
            "id_token",
            "access_token",
            "https://eai-api.enflux.com/is_superadmin",
            "https://eai-api.enflux.com/schools",
            "https://eai-api.enflux.com/user_id",
        ];
        for (let i = 0; i < keysToClear.length; i++) {
            localStorage.removeItem(keysToClear[i]);
        }
    };

    static clearAllStorage = () => {
        const chosenSchool = localStorage.getItem("chosen_school");
        localStorage.clear();
        if (chosenSchool) localStorage.setItem("chosen_school", chosenSchool);
        sessionStorage.clear();
        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
    };

    static redirectToLogin = () => {
        location.replace(CONFIG.paths.base + "login");
    };

    static humanSort = (list: any[], getter: (obj: any) => string) => {
        const sortFunc = (objA, objB) => getter(objA).localeCompare(getter(objB), undefined, { numeric: true, sensitivity: "base" });
        return list.sort(sortFunc);
    };

    static getRouteBase = () => {
        const base = CONFIG.paths.base;
        if (CONFIG.paths.schoolIdParam) {
            return `${base}${CONFIG.paths.schoolIdParam}/`;
        }
        return base;
    };

    static getLinkBase = () => {
        const base = CONFIG.paths.base;
        return `${base}${Utils.getChosenSchool()}`;
    };

    static setTitle = (pageTitle?: string): void => {
        const title = "Enflux";
        const subTitle = pageTitle ? ` | ${pageTitle}` : "";
        document.title = `${title}${subTitle}`;
    };

    static current_view: string[] = [];
    static setViewName = (viewName: string, title?: string): void => {
        Utils.setTitle(title ? title : undefined);
        Utils.current_view = [viewName];
    };

    static getViewName = (): string | null => {
        if (Utils.current_view.length > 0) {
            return Utils.current_view[Utils.current_view.length - 1];
        }
        return null;
    };

    static pushViewName = (viewName: string): void => {
        Utils.current_view.push(viewName);
    };

    static popViewName = (): void => {
        if (Utils.current_view.length > 1) {
            Utils.current_view.pop();
        }
    };

    static createStorableDashboardName = (name: string, workbook: string): string => {
        const storable = name.replace(" ", "**") + "__" + workbook;
        return storable.slice(0, 145);
    };

    static getDisplayDashboardName = (name: string): string => {
        if (!name || name === "") return "";
        return name.split("__")[0].replace("**", " ");
    };

    static getWorkbookIdFromDashboardName = (name: string): string => {
        return name.split("__")[1];
    };

    static getActionplanUrl = (dashboardName?: string, actionplanId?: string): string => {
        const linkBase = Utils.getLinkBase();
        if (dashboardName) {
            const cleanedName = this.getUrlFriendlyString(dashboardName);
            if (actionplanId) {
                return `${linkBase}/dashboard/${cleanedName}/${actionplanId}`;
            }
            return `${linkBase}/dashboard/${cleanedName}`;
        }
        if (actionplanId) {
            return `${linkBase}/actionplans/${actionplanId}`;
        }
        return `${linkBase}/actionplans`;
    };

    static getDashboardViewUrl = (dashboardId: number, viewId: string, refreshView: number, actionplanId?: string) => {
        const linkBase = Utils.getLinkBase();
        if (actionplanId) {
            return `${linkBase}/dashboard/${dashboardId}/${actionplanId}?viewId=${viewId}&refreshView=${refreshView}`;
        }

        return `${linkBase}/dashboard/${dashboardId}?viewId=${viewId}&refreshView=${refreshView}`;
    };

    static getDashboardUrlNew = (dashboardName: string, viewId: string, refreshView: number, actionplanId?: string) => {
        const linkBase = Utils.getLinkBase();
        const cleanedName = this.getUrlFriendlyString(dashboardName);
        if (actionplanId) {
            return `${linkBase}/dashboard/${cleanedName}/${actionplanId}?viewId=${viewId}&refreshView=${refreshView}`;
        }


        return `${linkBase}/dashboard/${cleanedName}?viewId=${viewId}&refreshView=${refreshView}`;
    };

    static setStorageValues = (data: { exp: number, is_superadmin: boolean, schools: { school_id: string }[], is_masquerade?: boolean }, callback?: () => void) => {
        let selectedSchoolId = localStorage.getItem("chosen_school");
        localStorage.setItem("current_user", JSON.stringify(data));
        localStorage.setItem("expires_at", String(data.exp));
        localStorage.setItem("is_superadmin", String(data.is_superadmin));

        const schoolList = data.schools.map(({ school_id: schoolId }) => schoolId);
        localStorage.setItem("school_list", schoolList.join(","));
        CONFIG.schools = schoolList;

        if (data.hasOwnProperty("is_masquerade") && !!data.is_masquerade) {
            localStorage.setItem("isMasquerade", "true");
        } else {
            localStorage.removeItem("isMasquerade");
        }

        if (schoolList.length < 1) {
            // Shouldn't ever get here - if you are not in any schools your status should be inactive and
            //   auth0 log in should fail. Catch this case anyway
            Sentry.captureException(new Error(`User got tokens but is not active in any schools. ${data}`));
            alert("User configuration error, please contact customer support");
        }
        if (!selectedSchoolId || schoolList.findIndex(schoolId => schoolId === selectedSchoolId) < 0) {
            // If no value was previously set, or if the previously set value is not in the current user's schools
            //   take the first one from their list
            selectedSchoolId = schoolList[0];
        }

        Utils.setChosenSchoolInfo(selectedSchoolId);
        if (callback) {
            callback();
        }
    };

    static parseStorageValue = (key: string): any => {
        const value = localStorage.getItem(key);
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return JSON.parse(value);
        } catch (e) {
            // Something is wrong with the localStorage value, log them
            // out so the login process can set the values again.
            Sentry.addBreadcrumb({
                message: `Attempted to parse localStorage value ${key} as json, but encountered an error`,
                data: {
                    key: key,
                    value: value,
                }
            });
            Sentry.captureException(e);
            Utils.clearLocalstorage();
            Utils.redirectToLogin();
        }
    };

    static getCurrentUser = (): ICurrentUser | null => {
        return Utils.parseStorageValue("current_user");
    };

    static setChosenSchoolInfo = (schoolId: string) => {
        const currentUserInfo = Utils.parseStorageValue("current_user");
        const nextSchoolInfo = currentUserInfo.schools.find(school => school.school_id === schoolId);
        localStorage.setItem("chosen_school_info", JSON.stringify(nextSchoolInfo));
        localStorage.setItem("chosen_school", schoolId);
    };

    static getSchoolList = (): string[] => {
        if (CONFIG.schools !== undefined) {
            return CONFIG.schools;
        }
        let schoolList: any = localStorage.getItem("school_list");
        if (schoolList) {
            CONFIG.schools = schoolList.split(",");
            return CONFIG.schools;
        }
        const currentUser = Utils.getCurrentUser();
        if (currentUser !== null) {
            schoolList = currentUser.schools.map(school => school.school_id);
            localStorage.setItem("school_list", schoolList.join(","));
            CONFIG.schools = schoolList;
            return CONFIG.schools;
        }

        return [];
    };

    static getChosenSchool = () => {
        const chosen = localStorage.getItem("chosen_school");
        if (chosen === null) {
            Sentry.captureMessage("User is logged in, but no chosen_school is set");
        }
        return chosen;
    };

    static getChosenSchoolInfo = () => {
        return Utils.parseStorageValue("chosen_school_info");
    };

    static hasAdminAccess = () => {
        const userChosenSchoolInfo = Utils.getChosenSchoolInfo();
        if (!userChosenSchoolInfo) {
            return false;
        }
        return userChosenSchoolInfo.is_admin;
    };

    static hasKeyAdmin = () => {
        const userChosenSchoolInfo = Utils.getChosenSchoolInfo();
        return userChosenSchoolInfo.is_key_admin;
    };

    static isSuperAdmin = () => {
        return localStorage.getItem("is_superadmin") === "true";
    };

    static getSchoolOptions = async (): Promise<{ schoolId: string, displayName: string }[]> => {
        const client = new EAIClient();
        const resp = await client.fetchSchools("?fields=basic&select_cols=school_id,display_name");
        return resp.data.data.map(({ school_id: schoolId, display_name: displayName }) => ({ schoolId, displayName }));
    };

    static getIsMasquerading = () => {
        if (CONFIG.isMasquerading === undefined) {
            const isMasquerading = localStorage.getItem("isMasquerade");
            CONFIG.isMasquerading = isMasquerading === "true";
        }
        return CONFIG.isMasquerading;
    };

    // PRIVACY MESSAGE ON LOGIN
    static shouldSkipPrivacyMessage() {
        return localStorage.getItem("hidePrivacyMessage") === "true";
    }
    static setSkipPrivacyMessage() {
        localStorage.setItem("hidePrivacyMessage", "true");
    }

    // DATE TIME
    /**
     * Tries out using string straight up, then
     * tries format returned from the API.
     * If all else fails, it returns invalid date
     */

    static formatDate(date: Date, format: string) {
        const year = date.getFullYear().toString().slice(-2);
        const yearComplete = date.getFullYear().toString();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        let hours: any = date.getHours();
        const ampm = hours >= 12 ? "PM" : "AM";
        hours %= 12;
        hours = hours || 12;
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const second = String(date.getSeconds()).padStart(2, "0");
        const milisenconds = String(date.getMilliseconds());
        const dayOfWeek = date.toLocaleString("en-us", { weekday: "short" });
        const monthName = date.toLocaleString("en-us", { month: "short" });
        return format
            .replace("YYYY", yearComplete)
            .replace("YY", year)
            .replace("MM", month)
            .replace("DD", day)
            .replace("HH", hours)
            .replace("hh", hours)
            .replace("h", hours)
            .replace("mm", minutes)
            .replace("ss", second)
            .replace("SSS", milisenconds)
            .replace("a", ampm)
            .replace("A", ampm)
            .replace("ddd", dayOfWeek)
            .replace("MMM", monthName);

    }
    static getValidDateFromString(datetimeStr: string, format?: string) {
        const invalid = "Invalid Date";
        if (!datetimeStr) return invalid;
        let dt: any = new Date(datetimeStr);

        if (isNaN(dt.getTime())) {
            const dateOnlyStr = datetimeStr.substring(0, 10);
            dt = new Date(dateOnlyStr);
        }

        if (isNaN(dt.getTime())) {
            dt = undefined;
        }
        if (dt) {
            dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset());
        }
        return dt ? (format ? Utils.formatDate(dt, format) : Utils.formatDate(dt, "YYYY-MM-DD")) : invalid;
    }

    static parseISOLocalDate(s) {
        const b = s.split(/\D/).map(n => Number(n));
        return new Date(b[0], b[1] - 1, b[2]);
    }

    /**
     * Parse a date string in various formats and return a valid Date object.
     * Supports formats like MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, etc.
     * Returns null if the date string is invalid.
     */
    static parseUserDateInput(dateString: string): Date | null {
        if (!dateString || !dateString.trim()) {
            return null;
        }

        const cleanInput = dateString.trim();
        
        // Try multiple parsing strategies
        const parseAttempts = [
            // Standard Date constructor
            () => new Date(cleanInput),
            
            // Handle MM/DD/YYYY, M/D/YYYY formats
            () => {
                const separators = ['/', '-', '.'];
                for (const separator of separators) {
                    if (cleanInput.includes(separator)) {
                        const parts = cleanInput.split(separator);
                        if (parts.length === 3) {
                            const month = parseInt(parts[0], 10);
                            const day = parseInt(parts[1], 10);
                            let year = parseInt(parts[2], 10);
                            
                            // Handle 2-digit years
                            if (year < 100) {
                                year = year < 50 ? 2000 + year : 1900 + year;
                            }
                            
                            if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year > 1900 && year < 3000) {
                                return new Date(year, month - 1, day);
                            }
                        }
                    }
                }
                throw new Error('No valid separator format found');
            },
            
            // Handle YYYY-MM-DD format
            () => {
                const isoMatch = cleanInput.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
                if (isoMatch) {
                    const year = parseInt(isoMatch[1], 10);
                    const month = parseInt(isoMatch[2], 10);
                    const day = parseInt(isoMatch[3], 10);
                    return new Date(year, month - 1, day);
                }
                throw new Error('Not ISO format');
            }
        ];

        for (const attempt of parseAttempts) {
            try {
                const date = attempt();
                if (date && !isNaN(date.getTime()) && date.getFullYear() > 1900 && date.getFullYear() < 3000) {
                    return date;
                }
            } catch (e) {
                // Continue to next parsing attempt
            }
        }

        return null;
    }

    /**
     * The Fabric DatePicker component sets the time to 00:00:00 when picking a date.
     * That seems unintuitive, since we're choosing due dates, so here we're adding
     * 23 hours and 59 minutes to set the timestamp to end of selected date.
     */
    static changeDatePickerTimeStampToEOD(time: string | Date, format = "YYYY-MM-DD HH:mm:ss.SSS") {
        let date: Date;
        if (typeof time === "string") {
            date = new Date(time);
        } else {
            date = new Date(time.getTime());
        }
        date.setHours(23, 59, 59, 999);
        return Utils.formatDate(date, format);
    }

    // LISTS
    static isNextButtonDisabled(pageNumber: number, pageSize: number, total: number) {
        if (pageSize * (pageNumber + 1) === total) return true;
        else return pageNumber >= Math.floor(total / pageSize);
    }

    static getCurrentYear = (): string => {
        return (new Date()).getFullYear().toString();
    };

    static sanitizeFileName = (fileName: string): string => {
        // Replaces /, \, ?, :, *, ", and whitespace in a string with a _
        const BAD_CHARS = /[\/\\?:*"\s]/g;
        return fileName.replace(BAD_CHARS, "_");
    };

    static studentToStudentLabel = (s: Student) => {
        const id = s.schools_student_id ? s.schools_student_id : s.student_email;
        return `${s.student_name} (${id})`;
    };

    static blobToAttachmentFile = (blob: Blob): AttachmentFile => {
        // Assumes that the blob is from a snapshot, name will be 'Snapshot YYYY-MM-DD.png' using current date
        const ret: any = blob;

        const name = `snapshot-${Utils.formatDate(new Date(), "YYYY-MM-DD")}.png`;

        ret.lastModified = new Date();
        ret.name = name;
        ret.sanitizedName = name;

        return ret as AttachmentFile;
    };

    static getBlobPromise = (store: SnapshotStore): undefined | Promise<Blob> => {
        // Getting the snapshot blob from the tableau extension happens via postMessage
        //  so in order to wait until we get the response, we use a reaction to the value
        //  changing. The value of snapshotBlob will only be changed if the extension is
        //  present, so if the dashboard doesn't have the extension we return undefined.
        if (store.extensionPresent) {
            return new Promise(resolve => {
                reaction(
                    () => store.snapshotBlob,
                    (snapshotBlob: Blob) => {
                        resolve(snapshotBlob);
                    },
                );
            });
        }
        return undefined;
    };

    static getUrlPromise = (store: SnapshotStore): Promise<string> => {
        return new Promise(resolve => {
            reaction(
                () => store.snapshotUrl,
                (snapshotUrl: string) => {
                    resolve(snapshotUrl);
                },
            );
        });
    };

    static getSnapshotAttachmentFileAndUrl = (store: SnapshotStore, useCache?: boolean): Promise<{ url: string, attachmentFile: undefined | AttachmentFile }> => {
        return new Promise(resolve => {
            const urlPromise = useCache ? this.urlPromise || Utils.getUrlPromise(store) : Utils.getUrlPromise(store);
            const blobPromise = Utils.getBlobPromise(store);

            if (blobPromise !== undefined) {
                // eslint-disable-next-line no-console
                console.log("Using extension to get dashboard snapshot");
                const timedBlobPromise = new DeadlinePromise<Blob>((timedResolve) => {
                    store.triggerSnapshot();
                    blobPromise.then((blob) => {
                        timedResolve(blob);
                    });
                }, 15000);
                Promise.all([urlPromise, timedBlobPromise]).then(responseArr => {
                    const url = responseArr[0];
                    const attachmentFile = responseArr[1] !== undefined ? Utils.blobToAttachmentFile(responseArr[1]) : undefined;
                    resolve({ url, attachmentFile });
                });
            }
            urlPromise.then((url) => {
                resolve({url, attachmentFile: undefined});
            });
            store.triggerSnapshot();
            this.urlPromise = urlPromise;
        });
    };

    static commaStringToList = (str: string): string[] => {
        // Converts a comma separated string in to a list.
        // Empty strings are not included in the output.
        return str
            .split(",")
            .map((entry: string) => entry.trim())
            .filter((entry: string) => entry !== "");
    };

    static optionToList = (options: LabelValueOption[]): string[] => {
        return options
            .map((entry: LabelValueOption) => entry.value);
    };

    static listToCommaString = (list: string[]): string => {
        // Converts a list of string in to a comma separated list.
        // Any empty strings in the input list are removed.
        return list
            .filter((entry: string) => entry !== "")
            .map((entry: string) => entry.trim())
            .join(", ");
    };

    static listToOption = (list: string[]): LabelValueOption[] => {
        // Converts a list of string in to a comma separated list.
        // Any empty strings in the input list are removed.
        return list
            .map((entry: string) => ({ label: entry, value: entry }));
    };

    static setWithExpiry = (key: string, value: string, ttl: number) => {
        const now = new Date();

        const item = {
            value: value,
            expiry: now.getTime() + ttl
        };
        localStorage.setItem(key, JSON.stringify(item));
    };

    static getWithExpiry = (key: string) => {
        const itemStr = localStorage.getItem(key);

        if (!itemStr) {
            return null;
        }

        const item = JSON.parse(itemStr);
        const now = new Date();
        if (now.getTime() > item.expiry) {
            // If the item is expired, return null
            localStorage.removeItem(key);
            return null;
        }
        return item.value;
    };

    static getPageCount = (totalCount: number, pageSize: number): number => {
        return Math.ceil(totalCount / pageSize) || 0;
    };

    static isFeatureActive(component: string, name: string, features: FeatureGating[]) {
        const found = features.find(f => f.component === component && f.name === name && f.enabled);

        return !!found;
    }

    static isFeatureActiveSWC(component: string, name: string, features: FeatureGatingSWC[]) {
        const found = features.find(f => f.component === component && f.name === name && f.enabled);

        return !!found;
    }
    static isUrl(urlStr: string) {
        try {
            new URL(urlStr);
            return true;
        } catch(e) {
            return false;
        }
    }

    static getYoutubeId(url: string) {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        if (match && match[2].length === 11) {
            return match[2];
        } else {
            return null;
        }
    }

    static getEAIClientConfigs = () => {
        const endpoint = CONFIG.apis.eai || "https://api.prod.enflux.com";
        const school = localStorage.getItem("chosen_school");

        return {
            endpoint,
            school,
            // base url with selected school
            baseUrl: `${endpoint}/schools/${school}`,
        };
    };

    static isValidUuid(uuid: any) {
        const uuidRegex = new RegExp("^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$");
        return uuidRegex.test(uuid);
    }

    static encodeBase64(str: string) {
        return Buffer.from(str, "utf-8").toString("base64");
    }

    static decodeBase64(str: string) {
        return Buffer.from(str, "base64").toString("utf-8");
    }

    static getUrlFriendlyString(str: string) {
        const newFriendlyUrl = str.replace(/[^a-zA-Z0-9]/g, "-");
        // remove hyphens at the end of the string
        return newFriendlyUrl.replace(/-+$/, "");
    }

    static generateQueryParams = (params: Record<string, string>) => {
        return Object.keys(params)
            .map(key => `${key}=${params[key]}`)
            .join("&");
    };

    static isYearInAcadYear(year: number, acadYear: string) {
        const [startYearStr, endYearStr] = acadYear.split("-");
        const startYear = parseInt(startYearStr, 10);
        const endYear = parseInt(endYearStr, 10);
        if (isNaN(startYear) || isNaN(endYear)) return false;
        return year >= startYear && year <= endYear;
    }
}

/** a class for deafults used across all of app */
export class Defaults {

    static tablePageSize() {
        return 50;
    }
    static tablePage() {
        return 0;
    }

    static tablePageSizeOpts() {
        return [10, 15, 25, 50, 75, 100];
    }
}

